import { useState } from "react";
import type { WorldData } from "../../../domain/types";
import { formatIlsCategory } from "../../../domain/rules/aircraft";
import { formatAircraftStatus, formatAirspaceRisk, formatAirspaceStatus, formatProgramStatus } from "../../format";
import { matchesQuery } from "./GmShared";


type WorldReferenceView = "airports" | "airspaces" | "regions" | "fuel" | "aircraftTypes";

export function WorldReference({ world }: { world: WorldData }) {
  const [view, setView] = useState<WorldReferenceView>("airports");
  const [query, setQuery] = useState("");
  const filteredAirports = world.airports.filter((airport) =>
    matchesQuery([airport.code, airport.icao, airport.nameZh, airport.cityZh, airport.countryZh, airport.regionId, airport.fuelZoneId], query)
  );
  const filteredAirspaces = world.airspaces.filter((airspace) => matchesQuery([airspace.id, airspace.nameZh, airspace.risk, airspace.regionIds.join(" ")], query));
  const filteredRegions = world.regions.filter((region) => matchesQuery([region.id, region.nameZh, region.nameEn, region.licenseCode, region.fuelZoneId, region.demandIndex], query));
  const filteredFuelZones = world.fuelZones.filter((zone) => matchesQuery([zone.id, zone.nameZh, zone.baseUsdPerTon, zone.taxIndex], query));
  const filteredAircraftTypes = world.aircraftTypes.filter((type) =>
    matchesQuery([type.id, type.family, type.name, type.rangeKm, type.seatCapacity, type.turnMinutes, type.fuelProfile.cruiseFuelKgPerHour, type.fuelProfile.holdingFuelKgPerMin], query)
  );

  return (
    <div className="world-reference enhanced">
      <div className="reference-tabs">
        {worldReferenceTabs(world).map((tab) => (
          <button key={tab.id} className={worldReferenceTabClassName(view, tab.id)} onClick={() => setView(tab.id)}>
            {tab.label}
            <em>{tab.count}</em>
          </button>
        ))}
      </div>
      <div className="gm-filter-bar reference-toolbar">
        <label>
          资料筛选
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="代码/名称/区域/能力" />
        </label>
      </div>

      {view === "airports" && (
        <div className="table-wrap compact">
          <table>
            <thead>
              <tr>
                <th>机场</th>
                <th>城市/国家</th>
                <th>区域</th>
                <th>油区</th>
                <th>时区</th>
                <th>席位</th>
                <th>进近</th>
              </tr>
            </thead>
            <tbody>
              {filteredAirports.map((airport) => (
                <tr key={airport.code}>
                  <td>{airport.code} / {airport.icao}<br />{airport.nameZh}</td>
                  <td>{airport.cityZh} / {airport.countryZh}</td>
                  <td>{airport.regionId}</td>
                  <td>{airport.fuelZoneId}</td>
                  <td>UTC{formatUtcOffset(airport.utcOffsetMinutes)}</td>
                  <td>{airport.slotCapacity}</td>
                  <td>{formatIlsCategory(airport.requiredIlsCategory)}</td>
                </tr>
              ))}
              {filteredAirports.length === 0 && (
                <tr>
                  <td colSpan={7}>没有匹配的机场。</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {view === "airspaces" && (
        <div className="table-wrap compact">
          <table>
            <thead>
              <tr>
                <th>空域</th>
                <th>覆盖区域</th>
                <th>风险</th>
                <th>基线开放</th>
              </tr>
            </thead>
            <tbody>
              {filteredAirspaces.map((airspace) => (
                <tr key={airspace.id}>
                  <td>{airspace.id}<br />{airspace.nameZh}</td>
                  <td>{airspace.regionIds.join(" / ")}</td>
                  <td>{formatAirspaceRisk(airspace.risk)}</td>
                  <td>{baselineAirspaceStatusLabel(airspace.baselineOpen)}</td>
                </tr>
              ))}
              {filteredAirspaces.length === 0 && (
                <tr>
                  <td colSpan={4}>没有匹配的空域。</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {view === "regions" && (
        <div className="table-wrap compact">
          <table>
            <thead>
              <tr>
                <th>区域</th>
                <th>英文</th>
                <th>航权</th>
                <th>客流指数</th>
                <th>油区</th>
              </tr>
            </thead>
            <tbody>
              {filteredRegions.map((region) => (
                <tr key={region.id}>
                  <td>{region.id}<br />{region.nameZh}</td>
                  <td>{region.nameEn}</td>
                  <td>{region.licenseCode}</td>
                  <td>{region.demandIndex}</td>
                  <td>{region.fuelZoneId}</td>
                </tr>
              ))}
              {filteredRegions.length === 0 && (
                <tr>
                  <td colSpan={5}>没有匹配的区域。</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {view === "fuel" && (
        <div className="table-wrap compact">
          <table>
            <thead>
              <tr>
                <th>油区</th>
                <th>基准油价</th>
                <th>税费指数</th>
              </tr>
            </thead>
            <tbody>
              {filteredFuelZones.map((zone) => (
                <tr key={zone.id}>
                  <td>{zone.id}<br />{zone.nameZh}</td>
                  <td>${zone.baseUsdPerTon}/t</td>
                  <td>{zone.taxIndex}</td>
                </tr>
              ))}
              {filteredFuelZones.length === 0 && (
                <tr>
                  <td colSpan={3}>没有匹配的油区。</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {view === "aircraftTypes" && (
        <div className="table-wrap compact">
          <table>
            <thead>
              <tr>
                <th>机型</th>
                <th>航程</th>
                <th>座位</th>
                <th>油耗</th>
                <th>标准过站</th>
                <th>MTOW</th>
                <th>检修间隔</th>
              </tr>
            </thead>
            <tbody>
              {filteredAircraftTypes.map((type) => (
                <tr key={type.id}>
                  <td>{type.id.toUpperCase()} / {type.family}<br />{type.name}</td>
                  <td>{type.rangeKm.toLocaleString("en-US")}km</td>
                  <td>{type.seatCapacity}</td>
                  <td>
                    巡航 {type.fuelProfile.cruiseFuelKgPerHour.toLocaleString("en-US")}kg/h
                    <br />
                    滑行 {type.fuelProfile.taxiFuelKgPerMin}kg/m / 等待 {type.fuelProfile.holdingFuelKgPerMin}kg/m
                  </td>
                  <td>{type.turnMinutes}m</td>
                  <td>{Math.round(type.maxTakeoffWeightKg / 1000)}t</td>
                  <td>{type.maintenanceIntervalHours}h</td>
                </tr>
              ))}
              {filteredAircraftTypes.length === 0 && (
                <tr>
                  <td colSpan={7}>没有匹配的机型。</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function worldReferenceTabs(world: WorldData): Array<{ id: WorldReferenceView; label: string; count: number }> {
  return [
    { id: "airports", label: "机场", count: world.airports.length },
    { id: "airspaces", label: "空域", count: world.airspaces.length },
    { id: "regions", label: "区域", count: world.regions.length },
    { id: "fuel", label: "油区", count: world.fuelZones.length },
    { id: "aircraftTypes", label: "机型", count: world.aircraftTypes.length }
  ];
}

function formatUtcOffset(offsetMinutes: number): string {
  const sign = utcOffsetSign(offsetMinutes);
  const absolute = Math.abs(offsetMinutes);
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;
  return `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function worldReferenceTabClassName(activeView: WorldReferenceView, tabView: WorldReferenceView): string {
  if (activeView === tabView) {
    return "active";
  }
  return "";
}

function baselineAirspaceStatusLabel(baselineOpen: boolean): string {
  if (baselineOpen) {
    return "开放";
  }
  return "关闭";
}

function utcOffsetSign(offsetMinutes: number): string {
  if (offsetMinutes >= 0) {
    return "+";
  }
  return "-";
}
